import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import { isFeatureSurface } from '../../mesh/feature';
import { FEATURE_FACE_PAIR_BVH_REFINE_ITERATIONS } from '../../mesh/feature/acceleration/featureFaceSpatialQueryTypes';
import { queryClosestPointsBetweenFeatureFaceMeshesViaBvh } from '../../mesh/feature/acceleration/featureFacePairClosestQuery';
import { projectPointOnFeatureSurface } from '../../reference/shared/projectPointOnFeatureSurface';
import {
    areMeasureNormalsParallel,
    resolveBoundedPlanarFaceWorldPlane,
} from './measureBoundedPlanarFace';
import { computeApproximateClosestPointsBetweenMeshFaces } from './measureMeshFaceApproximateClosestPoints';
import { collectFeatureFaceBoundaryWorldVertices } from './measureMeshFaceBoundaryVertices';

/** 判定收敛与退化距离的容差（世界单位）。 */
const MEASURE_CLOSEST_DISTANCE_EPSILON = 1e-9;

/** 面↔面 BVH 初解后的交替投影精化轮次。 */
const MESH_FACE_BVH_REFINE_MAX = FEATURE_FACE_PAIR_BVH_REFINE_ITERATIONS;

/** 曲面复杂度阈值：任一参与测量的面超过该三角形数量后直接使用近似最近点。 */
export const MEASURE_MESH_FACE_APPROXIMATE_TRIANGLE_THRESHOLD = 100;

/**
 * 两网格面最近点对。
 */
export interface MeshFaceClosestPointPair {
    /** 面 A 上的最近点。 */
    pointOnA: THREE.Vector3;
    /** 面 B 上的最近点。 */
    pointOnB: THREE.Vector3;
}

/**
 * 读取面代理三角网格上的唯一世界坐标顶点。
 *
 * @param face 面代理。
 * @returns 世界坐标顶点列表。
 */
export function collectFeatureFaceWorldVertices(face: FeatureFace): THREE.Vector3[] {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return [];
    }

    face.updateWorldMatrix(true, false);
    const indexAttribute = face.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count
        : positionAttribute.count;
    if (vertexCount <= 0) {
        return [];
    }

    const uniqueKeys = new Set<string>();
    const vertices: THREE.Vector3[] = [];
    const scratch = new THREE.Vector3();

    for (let cursor = 0; cursor < vertexCount; cursor += 1) {
        const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
            ? Number(indexAttribute.getX(cursor))
            : cursor;
        scratch.set(
            positionAttribute.getX(vertexIndex),
            positionAttribute.getY(vertexIndex),
            positionAttribute.getZ(vertexIndex),
        );
        scratch.applyMatrix4(face.matrixWorld);
        const key = `${scratch.x.toFixed(6)}:${scratch.y.toFixed(6)}:${scratch.z.toFixed(6)}`;
        if (uniqueKeys.has(key)) {
            continue;
        }
        uniqueKeys.add(key);
        vertices.push(scratch.clone());
    }

    return vertices;
}

/**
 * 读取特征面代理的三角形数量。
 *
 * @param face 面代理。
 * @returns 三角形数量；几何无效时返回 0。
 */
function resolveFeatureFaceTriangleCount(face: FeatureFace): number {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return 0;
    }

    const indexAttribute = face.geometry.getIndex();
    const vertexCursorCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count
        : positionAttribute.count;
    return Math.floor(vertexCursorCount / 3);
}

/**
 * 判断两面是否应直接使用近似最近点算法。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @returns 只要任一面超过复杂度阈值即返回 `true`。
 */
function shouldUseApproximateClosestPoints(
    faceA: FeatureFace,
    faceB: FeatureFace,
): boolean {
    return resolveFeatureFaceTriangleCount(faceA) > MEASURE_MESH_FACE_APPROXIMATE_TRIANGLE_THRESHOLD
        || resolveFeatureFaceTriangleCount(faceB) > MEASURE_MESH_FACE_APPROXIMATE_TRIANGLE_THRESHOLD;
}

/**
 * 直接求解复杂曲面近似最近点。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @returns 近似最近点对；近似查询失败时返回 `null` 并交由精确链路兜底。
 */
function computeApproximateMeshFaceClosestPointPair(
    faceA: FeatureFace,
    faceB: FeatureFace,
): MeshFaceClosestPointPair | null {
    const approximatePair = computeApproximateClosestPointsBetweenMeshFaces(faceA, faceB);
    if (!approximatePair) {
        return null;
    }

    return {
        pointOnA: approximatePair.pointOnA,
        pointOnB: approximatePair.pointOnB,
    };
}

/**
 * 比较并保留距离更小的最近点对。
 *
 * @param current 当前最优。
 * @param candidate 候选点对。
 * @returns 更优点对。
 */
function pickCloserMeshFacePair(
    current: MeshFaceClosestPointPair | null,
    candidate: MeshFaceClosestPointPair | null,
): MeshFaceClosestPointPair | null {
    if (!candidate) {
        return current;
    }
    if (!current) {
        return candidate;
    }
    const currentDistanceSq = current.pointOnA.distanceToSquared(current.pointOnB);
    const candidateDistanceSq = candidate.pointOnA.distanceToSquared(candidate.pointOnB);
    return candidateDistanceSq < currentDistanceSq ? candidate : current;
}

/**
 * 读取当前最优距离，用于 BVH 剪枝。
 *
 * @param pair 当前最优点对。
 * @returns 距离；无最优解时返回正无穷。
 */
function resolveMeshFacePairDistance(pair: MeshFaceClosestPointPair | null): number {
    if (!pair) {
        return Number.POSITIVE_INFINITY;
    }
    return pair.pointOnA.distanceTo(pair.pointOnB);
}

/**
 * 从面 A 上的种子点出发，交替投影求精最近点对。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param seedOnA 面 A 上的初始点。
 * @param maxIterations 最大迭代轮次。
 * @param maxThreshold 可选 BVH 剪枝距离。
 * @returns 最近点对；无法求解时返回 `null`。
 */
function refineMeshFacePairFromSeedOnA(
    faceA: FeatureFace,
    faceB: FeatureFace,
    seedOnA: THREE.Vector3,
    maxIterations: number = MESH_FACE_BVH_REFINE_MAX,
    maxThreshold?: number,
): MeshFaceClosestPointPair | null {
    let pointOnA = seedOnA.clone();
    const projectionOnB = projectPointOnFeatureSurface(faceB, pointOnA, { maxThreshold });
    if (!projectionOnB) {
        return null;
    }
    let pointOnB = projectionOnB.point;

    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
        const projectionOnA = projectPointOnFeatureSurface(faceA, pointOnB, { maxThreshold });
        if (!projectionOnA) {
            break;
        }

        const movedSq = pointOnA.distanceToSquared(projectionOnA.point);
        pointOnA = projectionOnA.point;

        const nextProjectionOnB = projectPointOnFeatureSurface(faceB, pointOnA, { maxThreshold });
        if (!nextProjectionOnB) {
            break;
        }
        pointOnB = nextProjectionOnB.point;

        if (movedSq <= MEASURE_CLOSEST_DISTANCE_EPSILON * MEASURE_CLOSEST_DISTANCE_EPSILON) {
            break;
        }
    }

    return { pointOnA, pointOnB };
}

/**
 * 从面 B 上的种子点出发，交替投影求精最近点对。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param seedOnB 面 B 上的初始点。
 * @param maxIterations 最大迭代轮次。
 * @param maxThreshold 可选 BVH 剪枝距离。
 * @returns 最近点对；无法求解时返回 `null`。
 */
function refineMeshFacePairFromSeedOnB(
    faceA: FeatureFace,
    faceB: FeatureFace,
    seedOnB: THREE.Vector3,
    maxIterations: number = MESH_FACE_BVH_REFINE_MAX,
    maxThreshold?: number,
): MeshFaceClosestPointPair | null {
    let pointOnB = seedOnB.clone();
    const projectionOnA = projectPointOnFeatureSurface(faceA, pointOnB, { maxThreshold });
    if (!projectionOnA) {
        return null;
    }
    let pointOnA = projectionOnA.point;

    for (let iteration = 0; iteration < maxIterations; iteration += 1) {
        const projectionOnB = projectPointOnFeatureSurface(faceB, pointOnA, { maxThreshold });
        if (!projectionOnB) {
            break;
        }

        const movedSq = pointOnB.distanceToSquared(projectionOnB.point);
        pointOnB = projectionOnB.point;

        const nextProjectionOnA = projectPointOnFeatureSurface(faceA, pointOnB, { maxThreshold });
        if (!nextProjectionOnA) {
            break;
        }
        pointOnA = nextProjectionOnA.point;

        if (movedSq <= MEASURE_CLOSEST_DISTANCE_EPSILON * MEASURE_CLOSEST_DISTANCE_EPSILON) {
            break;
        }
    }

    return { pointOnA, pointOnB };
}

/**
 * 将查询点投影到面 B 后，回投到面 A 形成候选点对。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param queryPoint 查询点（通常来自面 A 顶点）。
 * @param maxThreshold 可选 BVH 剪枝距离。
 * @returns 候选点对。
 */
function buildMeshFaceProjectionCandidate(
    faceA: FeatureFace,
    faceB: FeatureFace,
    queryPoint: THREE.Vector3,
    maxThreshold?: number,
): MeshFaceClosestPointPair | null {
    const projectionOnB = projectPointOnFeatureSurface(faceB, queryPoint, { maxThreshold });
    if (!projectionOnB) {
        return null;
    }
    const projectionOnA = projectPointOnFeatureSurface(faceA, projectionOnB.point, { maxThreshold });
    if (!projectionOnA) {
        return {
            pointOnA: queryPoint.clone(),
            pointOnB: projectionOnB.point.clone(),
        };
    }
    return {
        pointOnA: projectionOnA.point.clone(),
        pointOnB: projectionOnB.point.clone(),
    };
}

/**
 * 判断两平行有界平面是否可在法向投影上互相命中，从而适用无限平面法向距离。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param planeA 面 A 世界平面。
 * @param planeB 面 B 世界平面。
 * @returns 是否适用快速路径。
 */
function shouldUseParallelBoundedPlaneFastPath(
    faceA: FeatureFace,
    faceB: FeatureFace,
    planeA: { point: THREE.Vector3; normal: THREE.Vector3 },
    planeB: { point: THREE.Vector3; normal: THREE.Vector3 },
): boolean {
    if (isFeatureSurface(faceA) || isFeatureSurface(faceB)) {
        return false;
    }

    const planeGap = Math.abs(planeB.point.clone().sub(planeA.point).dot(planeA.normal));
    const tolerance = Math.max(1e-6, planeGap * 1e-4);

    const centerProjection = buildMeshFaceProjectionCandidate(faceA, faceB, planeA.point);
    if (!centerProjection) {
        return false;
    }

    const centerDistance = centerProjection.pointOnA.distanceTo(centerProjection.pointOnB);
    return Math.abs(centerDistance - planeGap) <= tolerance;
}

/**
 * 求两无限平行平面之间的最近点对（法向距离）。
 *
 * @param pointA 平面 A 上一点。
 * @param normalA 平面 A 单位法向。
 * @param pointB 平面 B 上一点。
 * @returns 最近点对。
 */
function closestPointsBetweenParallelPlanes(
    pointA: THREE.Vector3,
    normalA: THREE.Vector3,
    pointB: THREE.Vector3,
): MeshFaceClosestPointPair {
    const signedDistance = pointB.clone().sub(pointA).dot(normalA);
    const pointOnB = pointA.clone().addScaledVector(normalA, signedDistance);
    return {
        pointOnA: pointA.clone(),
        pointOnB,
    };
}

/**
 * 使用 BVH 求两有界网格面的全局最近点对初解。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @returns 最近点对；无法求解时返回 `null`。
 */
function computeInitialMeshFacePairViaBvh(
    faceA: FeatureFace,
    faceB: FeatureFace,
): MeshFaceClosestPointPair | null {
    const bvhPair = queryClosestPointsBetweenFeatureFaceMeshesViaBvh(faceA, faceB);
    if (!bvhPair) {
        return null;
    }
    return {
        pointOnA: bvhPair.pointOnA,
        pointOnB: bvhPair.pointOnB,
    };
}

/**
 * 对 BVH 初解执行有限轮交替投影精化。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param seed 初解点对。
 * @param maxThreshold 可选 BVH 剪枝距离。
 * @returns 精化后的最近点对。
 */
function refineMeshFacePairFromBvhSeed(
    faceA: FeatureFace,
    faceB: FeatureFace,
    seed: MeshFaceClosestPointPair,
    maxThreshold?: number,
): MeshFaceClosestPointPair | null {
    let best = seed;
    best = pickCloserMeshFacePair(
        best,
        refineMeshFacePairFromSeedOnA(faceA, faceB, seed.pointOnA, MESH_FACE_BVH_REFINE_MAX, maxThreshold),
    ) ?? best;
    best = pickCloserMeshFacePair(
        best,
        refineMeshFacePairFromSeedOnB(faceA, faceB, seed.pointOnB, MESH_FACE_BVH_REFINE_MAX, maxThreshold),
    ) ?? best;
    return best;
}

/**
 * 求解两有界网格面之间最近点对。
 *
 * 设计说明：
 * 1. 任一面超过复杂度阈值时直接返回近似最近点，避免复杂曲面测量阻塞交互；
 * 2. 近似查询失败时回落精确链路，保证异常几何不会导致测量无结果；
 * 3. 低复杂度面仍保持原有精确流程：平行有界平面解析快速路径；
 * 4. 一般面组合以 `closestPointToGeometry` BVH 求全局初解，再少量交替投影精化；
 * 5. 追加边界顶点采样兜底，替代原先的全顶点 O(V) 采样。
 *
 * @param faceA 面 A（三角网格面，支持任意 FeatureFace）
 * @param faceB 面 B（三角网格面，支持任意 FeatureFace）
 * @returns 最短距离点对对象（pointOnA, pointOnB），无法求解时返回 null
 */
export function computeClosestPointsBetweenMeshFaces(
    faceA: FeatureFace,
    faceB: FeatureFace,
): MeshFaceClosestPointPair | null {
    if (shouldUseApproximateClosestPoints(faceA, faceB)) {
        const approximatePair = computeApproximateMeshFaceClosestPointPair(faceA, faceB);
        if (approximatePair) {
            return approximatePair;
        }
    }

    const planeA = resolveBoundedPlanarFaceWorldPlane(faceA);
    const planeB = resolveBoundedPlanarFaceWorldPlane(faceB);
    if (
        planeA
        && planeB
        && areMeasureNormalsParallel(planeA.normal, planeB.normal)
        && shouldUseParallelBoundedPlaneFastPath(faceA, faceB, planeA, planeB)
    ) {
        return closestPointsBetweenParallelPlanes(
            planeA.point,
            planeA.normal,
            planeB.point,
        );
    }

    let best: MeshFaceClosestPointPair | null = null;

    const bvhSeed = computeInitialMeshFacePairViaBvh(faceA, faceB);
    if (bvhSeed) {
        const maxThreshold = resolveMeshFacePairDistance(bvhSeed);
        best = pickCloserMeshFacePair(best, refineMeshFacePairFromBvhSeed(faceA, faceB, bvhSeed, maxThreshold));
    }

    const centerMaxThreshold = resolveMeshFacePairDistance(best);
    best = pickCloserMeshFacePair(
        best,
        refineMeshFacePairFromSeedOnA(faceA, faceB, faceA.getWorldPoint(), MESH_FACE_BVH_REFINE_MAX, centerMaxThreshold),
    );
    best = pickCloserMeshFacePair(
        best,
        refineMeshFacePairFromSeedOnB(faceA, faceB, faceB.getWorldPoint(), MESH_FACE_BVH_REFINE_MAX, centerMaxThreshold),
    );

    const boundaryMaxThreshold = resolveMeshFacePairDistance(best);
    collectFeatureFaceBoundaryWorldVertices(faceA).forEach((vertex) => {
        best = pickCloserMeshFacePair(
            best,
            refineMeshFacePairFromSeedOnA(faceA, faceB, vertex, MESH_FACE_BVH_REFINE_MAX, boundaryMaxThreshold),
        );
    });

    collectFeatureFaceBoundaryWorldVertices(faceB).forEach((vertex) => {
        best = pickCloserMeshFacePair(
            best,
            refineMeshFacePairFromSeedOnB(faceA, faceB, vertex, MESH_FACE_BVH_REFINE_MAX, boundaryMaxThreshold),
        );
        const projectionOnA = projectPointOnFeatureSurface(faceA, vertex, { maxThreshold: boundaryMaxThreshold });
        if (projectionOnA) {
            best = pickCloserMeshFacePair(
                best,
                refineMeshFacePairFromSeedOnA(
                    faceA,
                    faceB,
                    projectionOnA.point,
                    MESH_FACE_BVH_REFINE_MAX,
                    boundaryMaxThreshold,
                ),
            );
        }
    });

    return best;
}
